import React, { useEffect, useState } from "react";
import { formatMoney, useGetPaymentCurrencyList } from "../../../utils";
import BudgetModal from "../../common/BudgetModal";
import { useTranslation } from "react-i18next";
import NewTooltips from "../../../assets/icons/NewTooltips";
import { useBudgetStore } from "../../../state/useBudgetStore";
import { useCompanyStore } from "../../../state/useCompanyStore";
import TooltipDropdown from "../../common/tooltipDropdown";

export interface BudgetHistoryWidgetProps {
  toolTipText?: string;
  width?: string; // Add a width prop
  height?: string; // Add a height prop
  selectedLabel?: string;
  showButton?: boolean;
}

const BudgetHistory: React.FC<BudgetHistoryWidgetProps> = ({
  toolTipText,
  width,
  height,
  selectedLabel,
  showButton,
}) => {
  const { t } = useTranslation("dashboard");
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const {
    budgets,
    fetchBudgets,
    isBudgetLoading: isLoading,
    error,
  } = useBudgetStore();
  const [actualExpensePercentage, setActualExpensePercentage] = useState<
    null | number
  >(null);
  const [monthlyBudgetPercentage, setMonthlyBudgetPercentage] = useState<
    null | number
  >(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const monthlyBudget = budgets[0]?.allocatedBudget ?? null;
  const actualExpenses = budgets[0]?.actualExpenses ?? null;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleOnClick = () => {
    setIsHovered(!isHovered);
  };

  const formatValue = (value: number) => {
    return value.toLocaleString();
  };

  const containerStyles = {
    width: width || "100%", // Apply the width prop or fallback to 100%
    height: height || "100%",
  };

  useEffect(() => {
    fetchBudgets();
  }, [showModal]);

  useEffect(() => {
    if (actualExpenses !== null && monthlyBudget !== null) {
      let percentage = 0;

      if (monthlyBudget > 0) {
        percentage = (actualExpenses / monthlyBudget) * 100;
      }

      // Allow overspending to exceed 100%
      const actualExpensePercentage = percentage;
      const monthlyBudgetPercentage = Math.max(100 - percentage, 0); // Avoid negative values

      setActualExpensePercentage(actualExpensePercentage);
      setMonthlyBudgetPercentage(monthlyBudgetPercentage);
    }
  }, [actualExpenses, monthlyBudget]);
  if (error) {
    return <div>Error: {error}</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (budgets.length == 0) {
    return (
      <>
        <div
          className="p-2 border flex flex-col justify-between border-[#EAECF0] rounded-lg h-full"
          style={containerStyles}
        >
          <div>
            <div className="flex justify-between items-center mb-5 lg:mb-7">
              <div className="font-normal flex items-center gap-2">
                <h2
                  className="text-[14px] text-[#667085] font-medium 1220:text-[13px] xl:text-[14px]  leading-5"
                  style={{ padding: "0" }}
                >
                  Budget
                </h2>
                {toolTipText && (
                  <div
                    className="relative cursor-pointer"
                    onClick={handleOnClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <TooltipDropdown
                      toolTipText={toolTipText}
                      isHovered={isHovered}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-md mt-5">
              <div className="mt-[2px] flex items-center justify-between w-full mb-3">
                <div
                  className="flex-grow-0 bg-[#EAECF0] w-[100%] h-[7px] rounded-md"
                  style={{ width: "100%" }}
                />
              </div>

              <div className="flex justify-between items-center">
                <div className="text-[14px]">
                  <h1 className="text-[#828282] font-medium leading-4">
                    Actual Expense
                  </h1>
                  <div className={`font-semibold leading-4 text-[16px] mt-1.5`}>
                    --
                  </div>
                </div>

                <div className=" text-end text-[14px]">
                  <h1 className="text-[#828282] font-medium leading-4">
                    {t("monthlyBudget")}
                  </h1>
                  <div className="font-semibold leading-4 text-[16px] mt-1.5">
                    --
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between mx-auto w-full  mb-3 items-center px-2">
            <button
              onClick={() => setShowModal(true)}
              className="border-[#044E97] mx-auto transition-all duration-500 hover:bg-[#044E97] hover:text-[#ffffff] p-[8px] border rounded-md text-[14px] font-medium text-[#044E97] "
            >
              Set Budget
            </button>
          </div>
        </div>

        {showModal && <BudgetModal setShowModal={setShowModal} />}
      </>
    );
  }

  const values = actualExpenses && monthlyBudget;
  return (
    <>
      <div
        className="pt-2 border border-[#EAECF0] flex justify-between flex-col  rounded-lg h-full"
        style={containerStyles}
      >
        <div className="px-2 pb-[10px] md:pb-[20px]">
          <div className="flex justify-between items-center">
            <div className="font-normal flex items-center gap-2">
              <h2 className="text-[14px] text-[#667085] font-medium 1220:text-[13px] xl:text-[14px]  leading-5">
                Budget
              </h2>
              {toolTipText && (
                <div
                  className="relative cursor-pointer"
                  onClick={handleOnClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <TooltipDropdown
                    toolTipText={toolTipText}
                    isHovered={isHovered}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="rounded-md mt-5">
            <div className="mt-[2px] flex items-center justify-between w-full mb-3">
              {monthlyBudget ? (
                <>
                  <div
                    className="flex-grow-0 bg-[#F79009] w-[100%] h-[4px] border-r border-[#F79009]"
                    style={{
                      width: values
                        ? `${actualExpensePercentage}%`
                        : actualExpenses
                          ? "100%"
                          : 0,
                    }}
                  />
                  <div
                    className="flex-grow-0 bg-[#0469C5] w-[100%] h-[4px]"
                    style={{
                      width: values
                        ? `${monthlyBudgetPercentage}%`
                        : monthlyBudget
                          ? "100%"
                          : 0,
                    }}
                  />
                </>
              ) : (
                <div
                  className="flex-grow-0 bg-[#F79009] w-[100%] h-[7px] rounded-md"
                  style={{ width: "100%" }}
                />
              )}
            </div>

            <div className="flex justify-between items-center">
              <div className="text-[14px]">
                <h1 className="text-[#828282] font-medium leading-4">
                  Actual Expense
                </h1>
                <div
                  className={`font-semibold leading-4 text-[16px] mt-1.5 text-[#F79009] ${
                    !monthlyBudget ? "text-[#F79009]" : ""
                  }`}
                >
                  {`${userCurrencyCode.currency} ${formatValue(actualExpenses || 0)}`}
                  <span className="text-gray-600 text-[14px]">
                    {actualExpensePercentage !== null &&
                      actualExpensePercentage > 0 &&
                      ` (${Math.round(actualExpensePercentage)}%)`}
                  </span>
                </div>
              </div>

              <div className="text-[#0469C5] text-end text-[14px]">
                <h1 className="text-[#828282] font-medium leading-4">
                  {t("monthlyBudget")}
                </h1>
                <div className="font-semibold leading-4 text-[16px] mt-1.5">
                  {monthlyBudget
                    ? `${formatMoney(monthlyBudget, userCurrencyCode.currency)}`
                    : `${formatMoney(0, userCurrencyCode.currency)}`}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          {showButton && (
            <div className="flex justify-between mx-auto w-full  mb-3 items-center px-2">
              <button
                onClick={() => setShowModal(true)}
                className="border-[#044E97] mx-auto transition-all duration-500 hover:bg-[#044E97] hover:text-[#ffffff] p-[8px] border rounded-md text-[14px] font-medium text-[#044E97] "
              >
                Update Budget
              </button>
            </div>
          )}
          {selectedLabel && (
            <div className="flex w-full bg-[#F9FAFB] justify-center items-center rounded-b-md border-t border-t-[#EAECF0]">
              <h6 className="w-full text-center text-[#667085] text-[14px] font-medium pt-[15px] pb-[15px]">
                This Month
              </h6>
            </div>
          )}
        </div>
      </div>
      {showModal && <BudgetModal setShowModal={setShowModal} />}
    </>
  );
};

export default BudgetHistory;
